import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PageShell from '../../components/common/PageShell';
import { LideraAIAnswer, lideraAIService } from '../../services/lideraAIService';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  references?: LideraAIAnswer['references'];
}

const suggestions = [
  'O que é a NR-1 e qual é sua finalidade?',
  'O que a NR-1 exige sobre o PGR?',
  'O que exige atenção na operação hoje?',
  'Quais treinamentos vencem nos próximos 30 dias?',
  'Quais exames estão vencidos?',
  'Procure o funcionário "João"',
  'Verifique a aptidão operacional de "João"',
  '"João" pode executar trabalho em altura?',
];

export default function LideraAIPage() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([{
    id: 1,
    role: 'assistant',
    text: 'Olá! Eu consulto fontes normativas oficiais e, na empresa ativa, vencimentos, pendências e situação operacional. Esta versão é somente leitura e não substitui parecer médico ou responsabilidade técnica.',
  }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event?: FormEvent, suggestion?: string) {
    event?.preventDefault();
    const question = (suggestion ?? input).trim();
    if (question.length < 2 || loading) return;
    const userMessage: Message = { id: Date.now(), role: 'user', text: question };
    setMessages((current) => [...current, userMessage]);
    setInput('');
    setError(null);
    setLoading(true);
    try {
      const response = await lideraAIService.chat(question);
      setMessages((current) => [...current, {
        id: Date.now() + 1,
        role: 'assistant',
        text: response.answer,
        references: response.references,
      }]);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível consultar a Lidera IA.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageShell title="Lidera IA" breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Lidera IA' }]}>
      <Stack spacing={2} sx={{ maxWidth: 960, mx: 'auto', width: '100%' }}>
        <Alert severity="info">
          As respostas usam os dados da empresa ativa. A Lidera IA não substitui parecer médico ou responsabilidade técnica.
        </Alert>

        <Card variant="outlined" sx={{ minHeight: 420 }}>
          <CardContent>
            <Stack spacing={2}>
              {messages.map((message) => (
                <Box key={message.id} sx={{ alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: message.role === 'user' ? 'primary.main' : 'action.hover', color: message.role === 'user' ? 'primary.contrastText' : 'text.primary', whiteSpace: 'pre-line' }}>
                    {message.role === 'assistant' && <SmartToyIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />}
                    <Typography component="span" variant="body1">{message.text}</Typography>
                  </Box>
                  {message.references && message.references.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {message.references.map((reference) => {
                        const external = /^https?:\/\//i.test(reference.link);
                        return (
                          <Chip
                            key={`${reference.link}-${reference.label}`}
                            label={reference.label}
                            clickable
                            component={external ? 'a' : 'div'}
                            {...(external
                              ? { href: reference.link, target: '_blank', rel: 'noreferrer' }
                              : { onClick: () => navigate(reference.link) })}
                            size="small"
                          />
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              ))}
              {loading && <CircularProgress size={24} />}
            </Stack>
          </CardContent>
        </Card>

        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {suggestions.map((suggestion) => (
            <Chip key={suggestion} label={suggestion} onClick={() => void submit(undefined, suggestion)} disabled={loading} />
          ))}
        </Stack>

        <Box component="form" onSubmit={(event) => void submit(event)} sx={{ display: 'flex', gap: 1 }}>
          <TextField fullWidth value={input} onChange={(event) => setInput(event.target.value)} placeholder="Pergunte sobre NRs, vencimentos, pendências ou aptidão operacional" inputProps={{ maxLength: 2000 }} disabled={loading} />
          <Button type="submit" variant="contained" endIcon={<SendIcon />} disabled={loading || input.trim().length < 2}>Enviar</Button>
        </Box>
      </Stack>
    </PageShell>
  );
}
